// pages/SapLogonAudit.jsx — SAP external access audit.
// ─────────────────────────────────────────────────────────────────────────────
// Menjawab satu pertanyaan: siapa mengakses SAP dari LUAR jaringan kantor,
// dari IP mana, dan memakai device apa. Backend: /api/sap/logon-audit/*.
//
// KEPUTUSAN TATA LETAK (bukan selera):
//   Pusat halaman ini adalah TABEL SESI, bukan form konfigurasi. Angka ambang
//   atau daftar CIDR tidak bisa dinilai benar/salah berdiri sendiri — yang bisa
//   dinilai adalah "sesi mana yang jadi tertandai". Karena itu form klasifikasi
//   dan hasilnya harus terlihat bersamaan, dan mengubah scope langsung memuat
//   ulang tabel tanpa perlu menyimpan konfigurasi dulu.
//
//   Spanduk metode di atas tabel SENGAJA permanen, tidak bisa ditutup. Data ini
//   berasal dari polling 5 menit, jadi sesi yang mulai dan berakhir di antara
//   dua polling tidak tertangkap. Siapa pun yang membaca angka di halaman ini
//   harus melihat batasan itu pada saat yang sama, bukan menemukannya nanti
//   setelah menyimpulkan sesuatu yang keliru.
//
//   Kolom Device menampilkan jenis DAN hostname ("Laptop · LUPIMIDJKTKNT10"):
//   jenisnya untuk pembaca non-teknis, hostname-nya untuk yang perlu melacak
//   unit fisiknya.
//
//   DUA SUMBER, SATU TABEL. Sejak 20 Agustus 2026 halaman ini menampilkan sesi
//   dari dua pengumpul yang berbeda: USR41 (SAPGUI/DIAG) dan TH_USER_LIST +
//   log nginx (HTTP/HTTPS, WebGUI/ITS). Keduanya menulis ke tabel yang sama dan
//   dibedakan kolom `source`. Penanda sumber ditempel MENYATU ke kolom Jalur
//   akses, bukan kolom ke-15 tersendiri: kolom baru itu akan kosong pada
//   mayoritas baris (semua sesi SAPGUI), sementara yang sebenarnya ingin
//   diketahui pembaca — "sesi ini datang lewat mana, dan seberapa yakin kita
//   soal IP-nya" — memang satu pertanyaan dengan jalur aksesnya.
//
//   IP sesi HTTP adalah HASIL KORELASI waktu dengan access log nginx, bukan
//   HOSTADDR. Tingkat keyakinannya karena itu HARUS terlihat: `proxy` pasti,
//   `proxy-ambiguous` berarti beberapa IP publik cocok pada jendela yang sama,
//   `proxy-unmatched` berarti tidak ada yang cocok sama sekali. Dua yang
//   terakhir ditandai amber dengan pola `(?)` yang sudah dipakai kolom Pemilik
//   device dan Asal — pada laporan audit, tebakan yang tampil seolah fakta
//   lebih berbahaya daripada kolom kosong.
//
// i18n: kamus lokal, pola sama seperti AwsCostBriefPage.jsx — translations.js
// berukuran ~570KB dan dipakai semua halaman; menambah kunci di sana untuk satu
// halaman berarti menyunting tiga blok bahasa tanpa cara memverifikasi hasilnya.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth, api as axiosApi } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { CronScheduler, useApi } from './tmShared';
import {
  ShieldAlert, RefreshCw, Save, Send, Eye, FileSpreadsheet, Loader2,
  Search, Info, CheckCircle2, AlertTriangle, Router, Globe, Building2, HelpCircle,
  Network, Plug, Monitor,
} from 'lucide-react';
import CollapsiblePanel from '../components/CollapsiblePanel';

const STR = {
  en: {
    title: 'SAP External Access Audit',
    subtitle: 'Who accessed SAP from outside the company network, and from which device',
    method: 'Two collectors run every 5 minutes. SAPGUI sessions come from table USR41; HTTP/HTTPS sessions (WebGUI/ITS) come from TH_USER_LIST — the same data as SM04/AL08 — and their real client IP is recovered by matching logon time against the nginx access log, because the proxy hides it. This is 5-minute sampling, not a complete record: sessions that start and end between two polls are never captured.',
    scope: 'Scope', external: 'External only', all: 'All sessions',
    source: 'Source', srcAll: 'All', srcGui: 'SAPGUI', srcHttp: 'HTTP',
    srcGuiHint: 'SAPGUI / DIAG sessions, from USR41.',
    srcHttpHint: 'WebGUI / ITS sessions, from TH_USER_LIST correlated with the nginx log.',
    viaProxy: 'Client IP recovered from the nginx access log — one public IP matched this logon time.',
    viaAmbiguous: 'Several different public IPs hit the WebGUI inside this session\u2019s time window, so this session cannot be attributed to any one of them. The empty IP is the correct answer, not a failure.',
    viaUnmatched: 'No WebGUI request in the nginx log matches this logon time. The session is real, but its origin IP is unknown — most often the nginx log had already rotated away (10 days retention).',
    viaDirect: 'Reached SAP directly, not through the nginx proxy — HOSTADDR is the client IP itself.',
    range: 'Range', days: 'days',
    refresh: 'Refresh', poll: 'Poll now', exportX: 'Export Excel',
    preview: 'Preview report', send: 'Send now', save: 'Save',
    sessions: 'Sessions', users: 'Distinct users',
    search: 'Search user / IP / host / device / SAPROUTER / HTTP…',
    clearFilter: 'Clear filter',
    chipHint: 'Click a path card to show only those sessions.',
    summaryPanel: 'Summary',
    detailPanel: 'Session detail',
    filterActive: 'filtered',
    user: 'SAP User', path: 'Access path', ip: 'IP Address', device: 'Device',
    owner: 'Device owner',
    ownerSuggested: 'Owner inferred by SDP from a scan, not assigned by an asset officer — treat as a hint, not a fact.',
    ownerNoAsset: 'This hostname is not in the SDP asset register.',
    ownerNoUser: 'The device is in SDP but has no owner assigned.',
    origin: 'Origin (public IP)',
    originAmb: 'more than one connection matched this time window — cannot be attributed',
    originNone: 'no matching SAProuter connection found',
    company: 'Company', site: 'Site', dept: 'Dept', client: 'Client',
    logon: 'Logon', firstSeen: 'First seen', lastSeen: 'Last seen', polls: 'Polls',
    sortAsc: 'Sort ascending', sortDesc: 'Sort descending', sortOff: 'Back to original order',
    noRows: 'No sessions recorded for this period.',
    noExternal: 'No external access recorded — every session in this period came from inside the company network.',
    settings: 'Settings',
    connId: 'SAP connection (profile id)',
    internalCidrs: 'Internal networks (CIDR, comma separated)',
    saprouterIps: 'SAProuter IP', vpnIps: 'VPN IP',
    emailTo: 'Recipients (To)', emailCc: 'CC', emailBcc: 'BCC',
    manualTest: 'Manual test',
    mtYear: 'Year (optional)',
    mtMonth: 'Month (optional)',
    mtXlsx: 'Download .xlsx',
    mtHint: 'Leave year and month empty to use the last closed month — the same period the schedule sends. Do not pick a month that is still running: it is incomplete and will understate the figures. A month entered here marks the email as [TEST] and does not affect the scheduled send.',
    confirmSend: 'Send this report by email now?',
    confirmRows: 'Rows in this period',
    confirmEmpty: 'This period has no sessions — the email will be empty.',
    rangeHint: 'Affects the table and Export Excel only. Preview and Send use the scheduled period.',
    periodMode: 'Scheduled report period',
    periodMonthly: 'Monthly (calendar month)',
    periodRolling: 'Rolling window (days)',
    periodDays: 'Rolling window (days)',
    periodDaysHintMonthly: 'Not used while the schedule is monthly — applies only to Preview report and Send now.',
    periodDaysHintRolling: 'Length of each scheduled report, counted back from the send date.',
    periodModeHint: 'Monthly sends the previous calendar month in full, on day 3 at 08:00 WIB.',
    reportTitle: 'Report title', preparedBy: 'Prepared by', senderName: 'Sender name',
    introNote: 'Context note (appears in every report)',
    introHint: 'Shown above the summary in each email and in the Excel Summary sheet. Use it for standing context — scope, ticket reference, who to contact. Leave empty to omit. Max 1000 characters.',
    orderNote: 'SAProuter and VPN are matched before the internal ranges, so an IP that sits inside an internal CIDR is still reported as external access.',
    lastPoll: 'Last poll', lastSent: 'Last report',
    never: 'never', saved: 'Settings saved.',
    polled: 'Poll finished', sent: 'Report sent',
    previewTitle: 'Report preview — nothing was sent',
    schedules: 'Automatic schedules',
    schedPoll: 'Collector — reads active SAP sessions and stores them. Without this, the report has no data.',
    schedReport: 'Sender — emails the report. Turn on only after recipients are filled in. Keep this schedule in step with the report period above: monthly period expects day 3 of each month.',
    schedRouter: 'SAProuter collector — reads saprouter.log and resolves the real public IP behind each SAProuter session. Without this, the Origin column stays empty. Keep its interval the same as the collector above: correlation matches two sides, and the one sampled less often sets the gap.',
    routerConfirm: 'Read the SAProuter log and correlate now?',
    adminOnly: 'Schedules can only be changed by an administrator.',
    pollConfirm: 'Run the collector once now?',
    reportConfirm: 'Send the report now? This will email the recipients.',

    httpTitle: 'HTTP source (nginx)',
    httpIntro: 'HTTP sessions reach SAP through the nginx proxy, so HOSTADDR only ever holds the proxy address. The real client IP is recovered by matching each session\u2019s logon time against the nginx access log, read over SSH. Without this, every WebGUI session from the internet would be classified by the proxy IP — which sits inside the internal ranges — and would silently vanish from the external access report.',
    httpEnabled: 'Collect HTTP sessions',
    httpEnabledHint: 'When off, the collector still records SAPGUI sessions as usual.',
    nginxHost: 'nginx host', nginxPort: 'SSH port', nginxUser: 'SSH user',
    nginxKey: 'Private key file',
    nginxKeyHint: 'File name only. Read from the dashboard keypairs directory; the key itself is never sent to the browser.',
    nginxLog: 'Access log path',
    proxyIp: 'Proxy IP as seen by SAP',
    proxyIpHint: 'Sessions whose HOSTADDR equals this are treated as proxied and are classified by the correlated IP instead.',
    pathMarker: 'WebGUI path marker',
    pathMarkerHint: 'Only log lines containing this path count as opening a session — assets and dashboard traffic do not.',
    winBack: 'Window before (s)', winFwd: 'Window after (s)',
    winHint: 'Correlation window around the logon time. Wider catches more sessions but produces more ambiguous ones.',
    wibOffset: 'WIB offset (hours)',
    wibOffsetHint: 'The nginx log is UTC and SAP session times are WIB. Changing this shifts every correlation.',
    gapWarn: 'Gap warning (hours)',
    gapWarnHint: 'nginx keeps only 10 days of logs. If the collector stops for longer than that, the missing origin IPs cannot be recovered afterwards.',
    testSsh: 'Test SSH', pollHttp: 'Poll HTTP now',
    saveHttp: 'Save HTTP settings',
    sshOk: 'SSH connection succeeded.',
    httpPolled: 'HTTP poll finished',
    ssh2Missing: 'The ssh2 module is not installed on the server — log reading is unavailable.',
    httpStats: 'Recorded HTTP sessions', nginxHits: 'nginx log entries',
    statsNone: 'no HTTP sessions recorded yet',
    detTitle: 'HTTP session detail', detHits: 'nginx log entries',
    detNone: 'Nothing recorded here yet.',
    detWindow: 'Correlation window', detCands: 'IPs seen in this window',
    detAnchorZeit: 'anchored on logon time (ZEIT)',
    detAnchorPoll: 'anchored on poll time — ZEIT was unreadable, so this window is less precise',
    detExpired: 'The nginx log lines for this window have already been deleted (7-day retention), so the competing IPs can no longer be listed. The recorded count still stands.',
    detAmbNote: 'These are the different public IPs that opened the WebGUI inside this session\u2019s window. Because several match, none of them can be attributed to this session.',
    detUnmNote: 'No WebGUI request matched this window at all — most often the log had already rotated away.',
    detClose: 'Close', detTid: 'TID', detLogon: 'Logon (WIB)', detHitCount: 'hits',
    detTopIps: 'Busiest source IPs', detRecent: 'Most recent entries',
    ambWatch: 'Ambiguous sessions are worth watching for a few days. If most HTTP sessions end up ambiguous, time alone is too weak a key and nginx should log the SAP client or session id instead.',
  },
  id: {
    title: 'Audit Akses SAP dari Luar',
    subtitle: 'Siapa mengakses SAP dari luar jaringan kantor, dan memakai device apa',
    method: 'Dua pengumpul berjalan tiap 5 menit. Sesi SAPGUI diambil dari tabel USR41; sesi HTTP/HTTPS (WebGUI/ITS) diambil dari TH_USER_LIST — data yang sama dengan SM04/AL08 — dan IP klien aslinya dipulihkan dengan mencocokkan waktu logon ke access log nginx, karena proxy menyembunyikannya. Ini sampling 5 menit, bukan pencatatan lengkap: sesi yang mulai dan berakhir di antara dua polling tidak pernah tertangkap.',
    scope: 'Cakupan', external: 'Hanya eksternal', all: 'Semua sesi',
    source: 'Sumber', srcAll: 'Semua', srcGui: 'SAPGUI', srcHttp: 'HTTP',
    srcGuiHint: 'Sesi SAPGUI / DIAG, dari USR41.',
    srcHttpHint: 'Sesi WebGUI / ITS, dari TH_USER_LIST yang dikorelasikan dengan log nginx.',
    viaProxy: 'IP klien dipulihkan dari access log nginx — satu IP publik cocok dengan waktu logon ini.',
    viaAmbiguous: 'Ada beberapa IP publik berbeda yang membuka WebGUI dalam jendela waktu sesi ini, sehingga sesi ini tidak dapat dikaitkan ke salah satunya. Kolom IP yang kosong adalah jawaban yang benar, bukan kegagalan.',
    viaUnmatched: 'Tidak ada request WebGUI di log nginx yang cocok dengan waktu logon ini. Sesinya nyata, tetapi IP asalnya tidak diketahui — paling sering karena log nginx sudah terhapus rotasi (retensi 10 hari).',
    viaDirect: 'Mencapai SAP langsung, tidak lewat proxy nginx — HOSTADDR memang IP kliennya sendiri.',
    range: 'Rentang', days: 'hari',
    refresh: 'Muat ulang', poll: 'Poll sekarang', exportX: 'Export Excel',
    preview: 'Pratinjau laporan', send: 'Kirim sekarang', save: 'Simpan',
    sessions: 'Sesi', users: 'User unik',
    search: 'Cari user / IP / host / device / SAPROUTER / HTTP…',
    clearFilter: 'Hapus saringan',
    chipHint: 'Tekan kartu jalur akses untuk menampilkan sesi kelas itu saja.',
    summaryPanel: 'Ringkasan',
    detailPanel: 'Rincian sesi',
    filterActive: 'tersaring',
    user: 'User SAP', path: 'Jalur akses', ip: 'Alamat IP', device: 'Device',
    owner: 'Pemilik device',
    ownerSuggested: 'Pemilik hasil tebakan SDP dari scan, bukan penetapan petugas aset — perlakukan sebagai petunjuk, bukan fakta.',
    ownerNoAsset: 'Hostname ini tidak ada di daftar aset SDP.',
    ownerNoUser: 'Device ada di SDP tetapi belum ada pemilik yang ditetapkan.',
    origin: 'Asal (IP publik)',
    originAmb: 'lebih dari satu koneksi cocok pada jendela waktu ini — tidak dapat dipastikan',
    originNone: 'tidak ditemukan koneksi SAProuter yang cocok',
    company: 'Perusahaan', site: 'Lokasi', dept: 'Dept', client: 'Client',
    logon: 'Logon', firstSeen: 'Pertama terlihat', lastSeen: 'Terakhir terlihat', polls: 'Polling',
    sortAsc: 'Urutkan naik', sortDesc: 'Urutkan turun', sortOff: 'Kembali ke urutan asli',
    noRows: 'Tidak ada sesi tercatat untuk periode ini.',
    noExternal: 'Tidak ada akses dari luar — semua sesi pada periode ini berasal dari dalam jaringan kantor.',
    settings: 'Pengaturan',
    connId: 'Koneksi SAP (id profil)',
    internalCidrs: 'Jaringan internal (CIDR, pisahkan koma)',
    saprouterIps: 'IP SAProuter', vpnIps: 'IP VPN',
    emailTo: 'Penerima (To)', emailCc: 'CC', emailBcc: 'BCC',
    manualTest: 'Uji manual',
    mtYear: 'Tahun (opsional)',
    mtMonth: 'Bulan (opsional)',
    mtXlsx: 'Unduh .xlsx',
    mtHint: 'Biarkan tahun dan bulan kosong untuk memakai bulan terakhir yang sudah tuntas — periode yang sama dengan kiriman terjadwal. Jangan memilih bulan yang masih berjalan: datanya belum lengkap dan angkanya akan terlihat lebih rendah. Bulan yang diisi di sini menandai email sebagai [TEST] dan tidak memengaruhi kiriman terjadwal.',
    confirmSend: 'Kirim laporan ini lewat email sekarang?',
    confirmRows: 'Jumlah baris pada periode ini',
    confirmEmpty: 'Periode ini tidak punya sesi — email akan kosong.',
    rangeHint: 'Hanya memengaruhi tabel dan Export Excel. Preview dan Send memakai periode terjadwal.',
    periodMode: 'Periode laporan terjadwal',
    periodMonthly: 'Bulanan (bulan kalender)',
    periodRolling: 'Jendela bergulir (hari)',
    periodDays: 'Jendela bergulir (hari)',
    periodDaysHintMonthly: 'Tidak dipakai selama jadwal bulanan — hanya berlaku untuk Preview report dan Send now.',
    periodDaysHintRolling: 'Panjang tiap laporan terjadwal, dihitung mundur dari tanggal kirim.',
    periodModeHint: 'Bulanan mengirim bulan kalender sebelumnya secara penuh, tanggal 3 pukul 08:00 WIB.',
    reportTitle: 'Judul laporan', preparedBy: 'Disiapkan oleh', senderName: 'Nama pengirim',
    introNote: 'Catatan konteks (muncul di setiap laporan)',
    introHint: 'Tampil di atas ringkasan pada tiap email dan di sheet Summary. Untuk konteks tetap — cakupan, nomor tiket, narahubung. Kosongkan bila tidak perlu. Maksimal 1000 karakter.',
    orderNote: 'SAProuter dan VPN dicocokkan lebih dulu daripada rentang internal, sehingga IP yang berada di dalam CIDR internal tetap dilaporkan sebagai akses dari luar.',
    lastPoll: 'Polling terakhir', lastSent: 'Laporan terakhir',
    never: 'belum pernah', saved: 'Pengaturan tersimpan.',
    polled: 'Polling selesai', sent: 'Laporan terkirim',
    previewTitle: 'Pratinjau laporan — tidak ada yang dikirim',
    schedules: 'Jadwal otomatis',
    schedPoll: 'Pengumpul — membaca sesi SAP aktif dan menyimpannya. Tanpa ini, laporan tidak punya data.',
    schedReport: 'Pengirim — mengirim laporan lewat email. Nyalakan hanya setelah penerima diisi. Selaraskan jadwal ini dengan periode laporan di atas: periode bulanan mengharapkan tanggal 3 tiap bulan.',
    schedRouter: 'Pengumpul SAProuter — membaca saprouter.log dan mencari IP publik asli di balik tiap sesi SAProuter. Tanpa ini, kolom Asal tetap kosong. Samakan intervalnya dengan pengumpul di atas: korelasi mencocokkan dua sisi, dan sisi yang lebih jarang di-sampling yang menentukan celahnya.',
    routerConfirm: 'Baca log SAProuter dan korelasikan sekarang?',
    adminOnly: 'Jadwal hanya bisa diubah oleh administrator.',
    pollConfirm: 'Jalankan pengumpul sekali sekarang?',
    reportConfirm: 'Kirim laporan sekarang? Email akan dikirim ke penerima.',

    httpTitle: 'Sumber HTTP (nginx)',
    httpIntro: 'Sesi HTTP mencapai SAP lewat proxy nginx, sehingga HOSTADDR selalu hanya berisi alamat proxy. IP klien yang sebenarnya dipulihkan dengan mencocokkan waktu logon tiap sesi ke access log nginx yang dibaca lewat SSH. Tanpa ini, seluruh sesi WebGUI dari internet akan diklasifikasi memakai IP proxy — yang berada di dalam rentang internal — dan lenyap dari laporan akses eksternal tanpa satu pun error.',
    httpEnabled: 'Kumpulkan sesi HTTP',
    httpEnabledHint: 'Saat mati, pengumpul tetap mencatat sesi SAPGUI seperti biasa.',
    nginxHost: 'Host nginx', nginxPort: 'Port SSH', nginxUser: 'User SSH',
    nginxKey: 'Berkas private key',
    nginxKeyHint: 'Nama berkas saja. Dibaca dari direktori keypairs dashboard; isi kuncinya tidak pernah dikirim ke browser.',
    nginxLog: 'Path access log',
    proxyIp: 'IP proxy dilihat dari SAP',
    proxyIpHint: 'Sesi yang HOSTADDR-nya sama dengan ini dianggap lewat proxy dan diklasifikasi memakai IP hasil korelasi.',
    pathMarker: 'Penanda path WebGUI',
    pathMarkerHint: 'Hanya baris log yang memuat path ini dihitung sebagai pembukaan sesi — berkas aset dan trafik dashboard tidak.',
    winBack: 'Jendela mundur (detik)', winFwd: 'Jendela maju (detik)',
    winHint: 'Jendela korelasi di sekitar waktu logon. Makin lebar makin banyak sesi tertangkap, tetapi makin banyak pula yang jadi ambigu.',
    wibOffset: 'Selisih WIB (jam)',
    wibOffsetHint: 'Log nginx memakai UTC sedangkan waktu sesi SAP memakai WIB. Mengubah angka ini menggeser seluruh korelasi.',
    gapWarn: 'Peringatan celah (jam)',
    gapWarnHint: 'nginx hanya menyimpan log 10 hari. Bila pengumpul mati lebih lama dari itu, IP asal yang hilang tidak bisa dipulihkan lagi kemudian.',
    testSsh: 'Uji SSH', pollHttp: 'Poll HTTP sekarang',
    saveHttp: 'Simpan pengaturan HTTP',
    sshOk: 'Koneksi SSH berhasil.',
    httpPolled: 'Poll HTTP selesai',
    ssh2Missing: 'Modul ssh2 tidak terpasang di server — pembacaan log tidak tersedia.',
    httpStats: 'Sesi HTTP tercatat', nginxHits: 'Entri log nginx',
    statsNone: 'belum ada sesi HTTP tercatat',
    detTitle: 'Detail sesi HTTP', detHits: 'Entri log nginx',
    detNone: 'Belum ada yang tercatat di sini.',
    detWindow: 'Jendela korelasi', detCands: 'IP yang terlihat di jendela ini',
    detAnchorZeit: 'berjangkar pada waktu logon (ZEIT)',
    detAnchorPoll: 'berjangkar pada waktu poll — ZEIT tidak terbaca, sehingga jendela ini kurang presisi',
    detExpired: 'Baris log nginx untuk jendela ini sudah terhapus (retensi 7 hari), sehingga IP yang bersaing tidak bisa lagi didaftar. Jumlah yang tercatat tetap berlaku.',
    detAmbNote: 'Berikut IP publik berbeda yang membuka WebGUI di dalam jendela sesi ini. Karena beberapa sama-sama cocok, tidak satu pun boleh dikaitkan ke sesi ini.',
    detUnmNote: 'Tidak ada request WebGUI yang cocok dengan jendela ini — paling sering karena log sudah terkena rotasi.',
    detClose: 'Tutup', detTid: 'TID', detLogon: 'Logon (WIB)', detHitCount: 'hit',
    detTopIps: 'IP sumber tersibuk', detRecent: 'Entri terbaru',
    ambWatch: 'Tingkat ambiguitas perlu diamati beberapa hari. Bila sebagian besar sesi HTTP berakhir ambigu, waktu saja terlalu lemah sebagai kunci dan nginx sebaiknya diminta mencatat SAP client atau session id.',
  },
};

// Warna per jalur akses. INTERNAL sengaja abu-abu netral: ia bukan temuan.
const CLASS_STYLE = {
  SAPROUTER: { bg: '#fff7ed', bd: '#fdba7455', fg: '#c2410c', Icon: Router },
  VPN:       { bg: 'rgb(var(--brand-50))', bd: '#93c5fd55', fg: 'rgb(var(--brand-700))', Icon: Globe },
  EXTERNAL:  { bg: '#fef2f2', bd: '#fca5a555', fg: '#b91c1c', Icon: ShieldAlert },
  UNKNOWN:   { bg: '#faf5ff', bd: '#d8b4fe55', fg: '#7e22ce', Icon: HelpCircle },
  INTERNAL:  { bg: '#f8fafc', bd: '#e2e8f0',   fg: '#475569', Icon: Building2 },
};

const fmtDT = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  return isNaN(d) ? String(s).slice(0, 19).replace('T', ' ')
    : d.toLocaleString('id-ID', { dateStyle: 'short', timeStyle: 'medium' });
};
const fmtSapDate = (s) => (/^\d{8}$/.test(String(s || '')) ? `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}` : (s || ''));
const fmtSapTime = (s) => (/^\d{6}$/.test(String(s || '')) ? `${s.slice(0, 2)}:${s.slice(2, 4)}:${s.slice(4, 6)}` : (s || ''));

// ── Pengurutan tabel rincian ────────────────────────────────────────────────
// Kolom tabel ini tidak semuanya berisi satu nilai skalar: Logon terdiri dari
// dua kolom SAP (LOGON_DATE + LOGON_TIME), Device menampung device_type dan
// hostname sekaligus, dan Origin menampilkan STATUS pencocokan — bukan selalu
// sebuah IP. Karena itu kunci urut ditentukan per kolom di SORT_KEYS, bukan
// diambil dari teks yang tampil: mengurutkan string tampilan akan menaruh
// "10.32.19.10" sebelum "9.9.9.9" dan "? ×3" di tengah-tengah daftar IP.
const ipKey = (v) => {
  const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(String(v || '').trim());
  // IPv6 atau hostname tidak dipaksa jadi angka; ia diurutkan sebagai teks
  // pada slot terpisah supaya tidak bercampur dengan urutan numerik IPv4.
  if (!m) return null;
  return ((+m[1] * 256 + +m[2]) * 256 + +m[3]) * 256 + +m[4];
};

// Origin punya tiga keadaan yang bermakna berbeda bagi pembaca audit; urutan
// ini menaruh yang paling pasti lebih dulu, lalu IP-nya sebagai kunci kedua.
const ORIGIN_RANK = { unique: 0, ambiguous: 1 };
const originRank = (r) => (r.origin_match in ORIGIN_RANK ? ORIGIN_RANK[r.origin_match] : 2);

const SORT_KEYS = {
  user:      { get: r => r.bname,                            tie: r => r.full_name },
  path:      { get: r => r.net_class,                        tie: r => (r.source === 'THUSER' ? 'HTTP' : 'SAPGUI') },
  ip:        { get: r => r.ip,                     kind: 'ip' },
  origin:    { get: r => originRank(r),            kind: 'num', tie: r => ipKey(r.origin_ip) },
  device:    { get: r => r.device_type,                      tie: r => r.hostname },
  owner:     { get: r => r.owner_name,                       tie: r => r.owner_email },
  company:   { get: r => r.company },
  site:      { get: r => r.city,                             tie: r => r.district },
  dept:      { get: r => r.dept_code },
  client:    { get: r => r.client,                 kind: 'num' },
  // Tanggal dan jam SAP berformat YYYYMMDD / HHMMSS, jadi digabung sebagai
  // teks sudah terurut secara kronologis tanpa perlu parse ke Date.
  // Tanpa tanggal, penggabungan ini harus menghasilkan string KOSONG, bukan
  // '000000' hasil padStart — nilai itu terbaca sebagai waktu paling awal dan
  // melompat ke puncak tabel, padahal baris tersebut tidak punya waktu logon
  // sama sekali dan tempatnya di bawah bersama nilai kosong lainnya.
  logon:     { get: r => (r.logon_date ? `${r.logon_date}${String(r.logon_time || '').padStart(6, '0')}` : '') },
  firstSeen: { get: r => r.first_seen,             kind: 'time' },
  lastSeen:  { get: r => r.last_seen,              kind: 'time' },
  polls:     { get: r => r.poll_count,             kind: 'num' },
};

const isBlank = (v) => v === null || v === undefined || v === '' || v === '—';

// Nilai kosong SELALU di bawah, ke arah mana pun sort dijalankan. Pada laporan
// audit, membaliknya membuat satu klik memenuhi layar dengan baris tanpa isi
// dan menyembunyikan data yang justru sedang dicari.
// NB: fungsi ini TIDAK mengurus nilai kosong. Kekosongan ditangani di cmpRows
// sebelum arah sort diterapkan, karena mengembalikan +1 dari sini akan ikut
// dinegasikan saat arah 'desc' — dan baris kosong justru naik ke atas.
const cmpVal = (a, b, kind) => {
  if (kind === 'num') {
    const na = Number(a), nb = Number(b);
    if (!isNaN(na) && !isNaN(nb)) return na - nb;
  }
  if (kind === 'time') {
    const da = new Date(a).getTime(), db = new Date(b).getTime();
    if (!isNaN(da) && !isNaN(db)) return da - db;
  }
  if (kind === 'ip') {
    const ia = ipKey(a), ib = ipKey(b);
    // IPv4 dibandingkan numerik dan selalu ditaruh sebelum yang bukan IPv4,
    // supaya kolom tidak melompat antara dua skema urutan.
    if (ia !== null && ib !== null) return ia - ib;
    if (ia !== null) return -1;
    if (ib !== null) return 1;
  }
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
};

// Blank-last berlaku juga untuk kunci pembanding kedua: dua baris dengan nilai
// utama sama tidak boleh saling menyalip hanya karena arah sort berubah.
const cmpStep = (a, b, kind, dir) => {
  // Kosong dibandingkan lebih dulu dan hasilnya TIDAK terpengaruh arah sort:
  // inilah yang menjaga baris tanpa isi tetap di bawah pada asc maupun desc.
  if (isBlank(a) && isBlank(b)) return 0;
  if (isBlank(a)) return 1;
  if (isBlank(b)) return -1;
  const c = cmpVal(a, b, kind);
  return dir === 'desc' ? -c : c;
};

const cmpRows = (ka, dir) => (ra, rb) => {
  const s = SORT_KEYS[ka];
  const c = cmpStep(s.get(ra), s.get(rb), s.kind, dir);
  if (c) return c;
  if (s.tie) {
    const t = cmpStep(s.tie(ra), s.tie(rb), s.tieKind, dir);
    if (t) return t;
  }
  return 0;
};

// Kartu ringkasan. Yang punya `onClick` menjadi saringan jalur akses: angkanya
// sudah ada di depan mata, dan menekan angka yang ingin ditelusuri lebih dekat
// ke cara orang membaca halaman ini daripada mencari kolom filter terpisah.
//
// Kartu Sessions dan Distinct users TIDAK diberi onClick — keduanya bukan
// kelas, jadi tidak ada yang bisa disaring darinya.
function Chip({ label, value, tone, onClick, active }) {
  const st = CLASS_STYLE[tone] || CLASS_STYLE.INTERNAL;
  const clickable = typeof onClick === 'function';
  return (
    <div
      onClick={onClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }) : undefined}
      style={{
        borderRadius: 10, background: st.bg, minWidth: 118,
        // Kartu terpilih ditandai garis tepi tebal berwarna kelasnya, bukan
        // sekadar warna latar yang berubah: latarnya sudah dipakai membedakan
        // kelas satu sama lain, dan menumpuk dua makna pada kanal yang sama
        // membuat keduanya sulit dibaca sekaligus.
        border: active ? `2px solid ${st.fg}` : `1px solid ${st.bd}`,
        padding: active ? '9px 13px' : '10px 14px',
        cursor: clickable ? 'pointer' : 'default',
        userSelect: clickable ? 'none' : 'auto',
      }}>
      <div style={{ fontSize: 10.5, letterSpacing: '.05em', textTransform: 'uppercase', color: '#64748b' }}>{label}</div>
      <div style={{ fontSize: 21, fontWeight: 700, color: st.fg, lineHeight: 1.25 }}>{value}</div>
    </div>
  );
}

function PathBadge({ value }) {
  const st = CLASS_STYLE[value] || CLASS_STYLE.UNKNOWN;
  const { Icon } = st;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 9px', borderRadius: 999,
      background: st.bg, border: `1px solid ${st.bd}`, color: st.fg, fontSize: 11.5, fontWeight: 600 }}>
      <Icon size={12} /> {value}
    </span>
  );
}

// Penanda sumber + tingkat keyakinan, ditempel DI BAWAH badge jalur akses.
//
// Menyatu, bukan kolom sendiri: `http_via` hanya terisi untuk baris 'THUSER',
// jadi kolom ke-15 akan kosong pada mayoritas baris. Yang lebih menentukan,
// pertanyaannya memang satu — "lewat mana, dan seberapa yakin kita soal IP-nya".
//
// Amber dipakai persis untuk keadaan yang TIDAK boleh dibaca sebagai fakta,
// pola yang sama dengan owner_source='suggested' dan origin_match='ambiguous'.
// `proxy` tidak diberi warna peringatan karena korelasinya tunggal dan pasti.
function SourceTag({ source, via, cands, T }) {
  if (source !== 'THUSER') return null;
  const amber = via === 'proxy-ambiguous' || via === 'proxy-unmatched';
  const title = via === 'proxy'           ? T.viaProxy
              : via === 'proxy-ambiguous' ? T.viaAmbiguous
              : via === 'proxy-unmatched' ? T.viaUnmatched
              : via === 'direct'          ? T.viaDirect : '';
  // Label sengaja pendek: barisnya sudah padat, dan penjelasan lengkapnya ada
  // di tooltip. `×N` menyebut berapa IP publik berbeda yang bersaing.
  const label = via === 'proxy'           ? 'HTTP · proxy'
              : via === 'proxy-ambiguous' ? `HTTP · ? \u00d7${cands || 2}`
              : via === 'proxy-unmatched' ? 'HTTP · ?'
              : via === 'direct'          ? 'HTTP · direct' : 'HTTP';
  return (
    <div title={title} style={{ marginTop: 3, fontSize: 10.5, fontWeight: 600, whiteSpace: 'nowrap',
      color: amber ? '#b45309' : '#64748b', cursor: title ? 'help' : 'default' }}>
      {label}
    </div>
  );
}

// `danger` dipakai HANYA oleh tombol yang mengirim email keluar. Warnanya bukan
// hiasan: tombol ini satu-satunya di halaman ini yang efeknya tidak bisa
// dibatalkan, dan harus terlihat berbeda dari tombol penjelajahan di sekitarnya.
function Btn({ onClick, disabled, busy, Icon, children, primary, danger }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled || busy}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 10,
        border: primary ? '1px solid #16794622' : danger ? '1px solid #fecaca' : '1px solid #e2e8f0',
        background: primary ? '#167946' : danger ? '#fef2f2' : '#fff',
        color: primary ? '#fff' : danger ? '#b91c1c' : '#334155',
        fontSize: 12.5, fontWeight: 600,
        cursor: (disabled || busy) ? 'not-allowed' : 'pointer',
        opacity: (disabled || busy) ? 0.6 : 1,
      }}>
      {busy ? <Loader2 size={13} className="animate-spin" /> : (Icon ? <Icon size={13} /> : null)}
      {children}
    </button>
  );
}

function Field({ label, hint, children }) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{ fontSize: 11.5, fontWeight: 600, color: '#475569', marginBottom: 4 }}>{label}</div>
      {children}
      {hint ? <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 3 }}>{hint}</div> : null}
    </label>
  );
}

const inputStyle = {
  width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid #e2e8f0',
  fontSize: 12.5, color: '#0f172a', background: '#fff',
};

// Modal detail kartu statistik HTTP.
//
// Dipisah jadi komponen sendiri dan menerima data yang SUDAH dimuat, bukan
// memuat sendiri: halaman induk yang tahu kartu mana yang ditekan, dan
// memindahkan pemuatan ke sini akan membuat modal sempat tampil kosong pada
// tiap pembukaan.
function HttpDetailModal({ open, kind, data, loading, onClose, T }) {
  if (!open) return null;
  const isHits = kind === 'hits';
  const rows = data?.rows || [];

  const note = kind === 'proxy-ambiguous' ? T.detAmbNote
             : kind === 'proxy-unmatched' ? T.detUnmNote : '';

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 90, background: 'rgba(15,23,42,.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 1000,
          maxHeight: '86vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 18px 48px rgba(15,23,42,.28)' }}>

        <div style={{ padding: '15px 20px', borderBottom: '1px solid #e2e8f0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ fontSize: 14.5, fontWeight: 700, color: '#0f172a' }}>
            {isHits ? T.detHits : T.detTitle}
            {!isHits && kind !== 'all' ? (
              <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 600,
                color: String(kind).startsWith('proxy-') ? '#b45309' : '#64748b',
                fontFamily: 'ui-monospace,monospace' }}>{kind}</span>
            ) : null}
          </div>
          <button onClick={onClose}
            style={{ border: '1px solid #e2e8f0', background: '#fff', borderRadius: 8,
              padding: '5px 12px', cursor: 'pointer', fontSize: 12, color: '#475569' }}>
            {T.detClose}
          </button>
        </div>

        <div style={{ padding: '16px 20px', overflowY: 'auto' }}>
          {note ? (
            <div style={{ fontSize: 11.5, lineHeight: 1.6, color: '#92400e', background: '#fffbeb',
              border: '1px solid #fde68a', borderRadius: 9, padding: '9px 13px', marginBottom: 14 }}>
              {note}
            </div>
          ) : null}

          {loading ? (
            <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8', fontSize: 12.5 }}>…</div>
          ) : rows.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8', fontSize: 12.5 }}>{T.detNone}</div>
          ) : isHits ? (
            <>
              {/* Ringkasan per IP didahulukan. Membaca 200 baris log satu per
                  satu jarang menjawab apa pun; yang menonjol justru IP mana
                  yang muncul jauh lebih sering daripada yang lain. */}
              <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase',
                letterSpacing: '.05em', marginBottom: 7 }}>{T.detTopIps}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginBottom: 18 }}>
                {(data?.by_ip || []).map(v => (
                  <div key={v.remote_addr} title={`${v.a} → ${v.b}`}
                    style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '5px 10px',
                      fontSize: 11.5, fontFamily: 'ui-monospace,monospace', color: '#334155' }}>
                    {v.remote_addr} <b style={{ color: '#167946' }}>{v.n}</b>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase',
                letterSpacing: '.05em', marginBottom: 7 }}>{T.detRecent}</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5 }}>
                <thead>
                  <tr style={{ background: '#f8fafc', color: '#475569', textAlign: 'left' }}>
                    <th style={{ padding: '7px 9px' }}>ts (UTC)</th>
                    <th style={{ padding: '7px 9px' }}>remote_addr</th>
                    <th style={{ padding: '7px 9px' }}>method</th>
                    <th style={{ padding: '7px 9px' }}>status</th>
                    <th style={{ padding: '7px 9px' }}>uri</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(h => (
                    <tr key={h.id} style={{ borderTop: '1px solid #f1f5f9', fontFamily: 'ui-monospace,monospace' }}>
                      <td style={{ padding: '6px 9px', whiteSpace: 'nowrap' }}>{String(h.ts_utc || '').replace('T', ' ').replace('.000Z', '')}</td>
                      <td style={{ padding: '6px 9px' }}>{h.remote_addr}</td>
                      <td style={{ padding: '6px 9px' }}>{h.method}</td>
                      <td style={{ padding: '6px 9px', color: String(h.status).startsWith('2') ? '#167946' : '#b45309' }}>{h.status}</td>
                      <td style={{ padding: '6px 9px', maxWidth: 420, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={h.uri}>{h.uri}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {rows.map(r => (
                <div key={r.id} style={{ border: '1px solid #e2e8f0', borderRadius: 11, padding: '12px 14px' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'baseline' }}>
                    <b style={{ fontSize: 13, color: '#0f172a', fontFamily: 'ui-monospace,monospace' }}>{r.bname}</b>
                    <span style={{ fontSize: 11.5, color: '#64748b' }}>{T.detTid} {r.sap_tid} · mandt {r.client}</span>
                    <span style={{ fontSize: 11.5, color: '#64748b' }}>
                      {T.detLogon} <b style={{ color: '#334155' }}>{fmtSapTime(r.logon_time)}</b></span>
                    <span style={{ fontSize: 11.5, fontWeight: 600, fontFamily: 'ui-monospace,monospace',
                      color: String(r.http_via || '').startsWith('proxy-') ? '#b45309' : '#167946' }}>
                      {r.http_via}{r.ip ? ` · ${r.ip}` : ''}</span>
                    <span style={{ fontSize: 11.5, color: '#94a3b8' }}>{r.net_class}</span>
                  </div>

                  {r.window ? (
                    <div style={{ marginTop: 7, fontSize: 11, color: '#64748b', fontFamily: 'ui-monospace,monospace' }}>
                      {T.detWindow}: {r.window.from.replace('T', ' ').replace('.000Z', '')} → {r.window.to.replace('T', ' ').replace('.000Z', '')}
                      <span style={{ fontFamily: 'inherit', marginLeft: 8,
                        color: r.window.anchor === 'zeit' ? '#94a3b8' : '#b45309' }}>
                        ({r.window.anchor === 'zeit' ? T.detAnchorZeit : T.detAnchorPoll})
                      </span>
                    </div>
                  ) : null}

                  {r.evidence_expired ? (
                    <div style={{ marginTop: 8, fontSize: 11, lineHeight: 1.6, color: '#92400e' }}>{T.detExpired}</div>
                  ) : r.candidates?.length ? (
                    <div style={{ marginTop: 9 }}>
                      <div style={{ fontSize: 10.5, color: '#94a3b8', textTransform: 'uppercase',
                        letterSpacing: '.05em', marginBottom: 5 }}>{T.detCands}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {r.candidates.map(c => (
                          <div key={c.remote_addr} title={`${c.a} → ${c.b}`}
                            style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '4px 9px',
                              fontSize: 11, fontFamily: 'ui-monospace,monospace', color: '#334155',
                              // IP yang akhirnya DIPAKAI ditandai hijau. Pada
                              // kasus ambigu tidak ada satu pun yang ditandai —
                              // memang itu inti persoalannya.
                              background: c.remote_addr === r.ip ? '#ecfdf5' : '#fff',
                              borderColor: c.remote_addr === r.ip ? '#a7f3d0' : '#e2e8f0' }}>
                            {c.remote_addr} <span style={{ color: '#94a3b8' }}>{c.n} {T.detHitCount}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SapLogonAudit() {
  const { lang } = useLang();
  const { user } = useAuth();
  const T = STR[lang === 'en' ? 'en' : 'id'];
  // CronScheduler memakai gaya fetch (path, {method, body}), berbeda dari
  // axiosApi yang dipakai halaman ini — jadi ia butuh adapter tersendiri.
  const cronApi = useApi();

  const [cfg, setCfg]         = useState(null);
  const [draft, setDraft]     = useState(null);
  const [data, setData]       = useState(null);
  const [days, setDays]       = useState(14);
  // Rentang tanggal eksplisit (dari - sampai). Default: 14 hari terakhir.
  const isoDay = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  const [dFrom, setDFrom] = useState(() => isoDay(new Date(Date.now() - 14 * 86400000)));
  const [dTo, setDTo] = useState(() => isoDay(new Date()));
  const [scope, setScope]     = useState('external');
  const [q, setQ]             = useState('');
  // Urutan bawaan SENGAJA kosong: tabel tampil apa adanya seperti yang dikirim
  // server sampai pembaca menekan sebuah kolom. Klik ketiga mengembalikan
  // keadaan ini — pada laporan audit, urutan asli adalah salah satu bentuk
  // datanya dan harus bisa dipulihkan tanpa memuat ulang halaman.
  const [sort, setSort]       = useState({ key: '', dir: 'asc' });
  const [loading, setLoading] = useState(false);
  const [busy, setBusy]       = useState('');
  const [msg, setMsg]         = useState(null);
  const [preview, setPreview] = useState(null);
  // Tahun/bulan untuk blok Manual test. Sengaja TIDAK tersimpan ke konfigurasi:
  // ini pilihan sekali pakai untuk memeriksa satu bulan, bukan setelan laporan.
  const [ym, setYm]           = useState({ year: '', month: '' });

  // Sumber disaring DI SISI KLIEN, tidak lewat query. Kedua sumber sudah dimuat
  // dalam satu permintaan yang sama, jadi menambah parameter server hanya akan
  // menambah bolak-balik jaringan tanpa mengubah apa pun yang terlihat.
  const [src, setSrc]         = useState('all');

  // Jalur akses yang sedang disaring, kosong = semua. Disimpan sebagai satu
  // nilai, bukan himpunan: pertanyaan yang lazim adalah "yang SAPROUTER saja
  // ada berapa", bukan gabungan dua kelas.
  const [cls, setCls]         = useState('');

  // Konfigurasi sumber HTTP punya endpoint TERSENDIRI (/api/sap/http-audit/*),
  // bukan bagian dari config logon-audit. Karena itu state, draft, dan tombol
  // Simpan-nya juga terpisah — menyimpan yang satu tidak boleh diam-diam ikut
  // menulis yang lain.
  const [hcfg, setHcfg]       = useState(null);
  const [hdraft, setHdraft]   = useState(null);
  const [hstats, setHstats]   = useState(null);
  const [sshOut, setSshOut]   = useState(null);

  // Modal detail: `kind` menyimpan kartu mana yang ditekan ('' = tertutup).
  const [det, setDet]         = useState('');
  const [detData, setDetData] = useState(null);
  const [detBusy, setDetBusy] = useState(false);

  // Dibungkus useCallback karena openDetail memakainya sebagai dependensi;
  // tanpa ini openDetail dibuat ulang tiap render tanpa alasan.
  const flash = useCallback((type, text) => {
    setMsg({ type, text }); setTimeout(() => setMsg(null), 6000);
  }, []);

  const loadCfg = useCallback(async () => {
    try {
      const r = await axiosApi.get('/api/sap/logon-audit/config');
      setCfg(r.data); setDraft(r.data);
      if (r.data?.period_days) setDays(r.data.period_days);
    } catch (e) { flash('error', e.response?.data?.error || e.message); }
  }, [flash]);

  // Parameter WAJIB, bukan berdefault ke state. Dengan begitu loadData tidak
  // menutup days/scope, dan identitasnya tetap stabil karena flash sendiri
  // sudah di-useCallback — efek di bawah tidak perlu penekanan aturan lint.
  const loadData = useCallback(async (d, s) => {
    setLoading(true);
    try {
      const r = await axiosApi.get(`/api/sap/logon-audit/data?days=${d}&scope=${s}&from=${dFrom}&to=${dTo}`);
      setData(r.data);
    } catch (e) { flash('error', e.response?.data?.error || e.message); }
    setLoading(false);
  }, [flash, dFrom, dTo]);

  // Konfigurasi dan statistik HTTP dimuat bersama: keduanya dibaca dari blok
  // yang sama di layar, dan memisahkannya hanya membuat salah satunya sempat
  // tampil basi. Kegagalannya TIDAK di-flash sebagai error — modul HTTP boleh
  // saja belum aktif, dan halaman ini tetap berguna penuh tanpanya.
  const loadHttp = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([
        axiosApi.get('/api/sap/http-audit/config'),
        axiosApi.get('/api/sap/http-audit/stats').catch(() => null),
      ]);
      setHcfg(c.data); setHdraft(c.data);
      if (s) setHstats(s.data);
    } catch (_) { setHcfg(null); setHdraft(null); }
  }, []);

  useEffect(() => { loadCfg(); }, [loadCfg]);
  useEffect(() => { loadHttp(); }, [loadHttp]);
  useEffect(() => { loadData(days, scope); }, [days, scope, loadData]);

  const act = async (name, fn) => {
    setBusy(name);
    try { await fn(); }
    catch (e) { flash('error', e.response?.data?.error || e.message); }
    setBusy('');
  };

  const doPoll = () => act('poll', async () => {
    const r = await axiosApi.post('/api/sap/logon-audit/poll');
    flash('ok', `${T.polled}: ${r.data.scanned} scanned, ${r.data.inserted} new, ${r.data.updated} updated.`);
    await loadData(days, scope);
  });

  const doSave = () => act('save', async () => {
    const r = await axiosApi.put('/api/sap/logon-audit/config', draft);
    setCfg(r.data); setDraft(r.data);
    flash('ok', T.saved);
    await loadData(days, scope);   // klasifikasi baru bisa mengubah isi tabel
  });

  // Data dimuat SETIAP kali modal dibuka, tidak di-cache: nilainya justru saat
  // menyelidiki poll yang baru saja berjalan, dan angka basi di layar
  // penyelidikan lebih buruk daripada jeda sepersekian detik.
  const openDetail = useCallback(async (kind) => {
    setDet(kind); setDetData(null); setDetBusy(true);
    try {
      const url = kind === 'hits'
        ? '/api/sap/http-audit/hits?limit=200'
        : `/api/sap/http-audit/sessions${kind === 'all' ? '' : `?via=${encodeURIComponent(kind)}`}`;
      const r = await axiosApi.get(url);
      setDetData(r.data);
    } catch (e) {
      setDetData({ rows: [] });
      flash('err', e?.response?.data?.error || e.message);
    } finally { setDetBusy(false); }
  }, [flash]);

  const doSaveHttp = () => act('hsave', async () => {
    const r = await axiosApi.put('/api/sap/http-audit/config', hdraft);
    setHcfg(r.data); setHdraft(r.data);
    flash('ok', T.saved);
  });

  // Uji SSH tidak menulis satu baris pun ke tabel sesi — hanya menyambung,
  // membaca satu baris log, dan menampilkan `id` agar keanggotaan grup logread
  // dapat diperiksa langsung. Aman ditekan kapan saja.
  const doTestSsh = () => act('ssh', async () => {
    const r = await axiosApi.post('/api/sap/http-audit/test-ssh');
    setSshOut(r.data.output || '');
    flash('ok', T.sshOk);
  });

  // Poll HTTP manual tidak mengirim email dan tidak menyentuh last_sent_period,
  // jadi tidak perlu konfirmasi maupun warna merah — sama derajatnya dengan
  // "Poll sekarang" di toolbar.
  const doPollHttp = () => act('hpoll', async () => {
    const r = await axiosApi.post('/api/sap/http-audit/poll');
    const d = r.data || {};
    flash('ok', `${T.httpPolled}: ${d.scanned ?? 0} scanned, ${d.inserted ?? 0} new, ${d.updated ?? 0} updated.`);
    await Promise.all([loadHttp(), loadData(days, scope)]);
  });

  // Blok Manual test memakai dua kolom terpisah, mengikuti pola halaman Monthly
  // Report agar kedua halaman berperilaku sama. Kosong = bulan terakhir yang
  // sudah tuntas, yaitu periode yang sama dengan kiriman terjadwal.
  const monthArgs = () => {
    const y = String(ym.year || '').trim();
    const m = String(ym.month || '').trim();
    if (!y && !m) return {};
    return { year: y, month: m };
  };

  const doPreview = () => act('preview', async () => {
    const r = await axiosApi.post('/api/sap/logon-audit/preview', monthArgs());
    setPreview(r.data);
  });

  // Tombol ini mengirim email sungguhan ke atasan dan penerima lain, dan
  // periodenya mengikuti mode terjadwal — BUKAN kolom Range di toolbar. Dalam
  // mode bulanan itu berarti bulan kalender SEBELUMNYA, yang bisa saja kosong
  // bila pengumpulan data baru dimulai bulan ini. Karena itu periode dan
  // jumlah barisnya dipastikan lebih dulu lewat pratinjau, lalu ditanyakan.
  // Mengirim laporan kosong ke atasan jauh lebih mahal daripada satu klik
  // konfirmasi.
  const doSend = () => act('send', async () => {
    const p = await axiosApi.post('/api/sap/logon-audit/preview', monthArgs());
    const rows = p.data?.rows ?? 0;
    const ok = window.confirm(
      `${T.confirmSend}\n\n${p.data?.subject || ''}\n\n${T.confirmRows}: ${rows}`
      + (rows ? '' : `\n\n${T.confirmEmpty}`));
    if (!ok) return;
    const r = await axiosApi.post('/api/sap/logon-audit/send', monthArgs());
    flash('ok', `${T.sent}: ${r.data.to}`);
    await loadCfg();
  });

  const doExport = () => act('export', async () => {
    const res = await axiosApi.get(`/api/sap/logon-audit/export?days=${days}&from=${dFrom}&to=${dTo}`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data],
      { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
    const a = document.createElement('a');
    a.href = url; a.download = `sap-external-access_${dFrom}_${dTo}.xlsx`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  });

  // Unduh lampiran untuk bulan tertentu. Berbeda dari Export Excel di toolbar,
  // yang mengikuti kolom Range: yang ini menghasilkan berkas yang identik dengan
  // lampiran email, sehingga yang diperiksa memang yang akan terkirim.
  const doExportMonth = () => act('xlsxMonth', async () => {
    const a2 = monthArgs();
    const qs = (a2.year && a2.month) ? `?year=${a2.year}&mon=${a2.month}` : '';
    const res = await axiosApi.get(`/api/sap/logon-audit/export${qs}`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data],
      { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = (a2.year && a2.month)
      ? `sap-external-access_${a2.year}-${String(a2.month).padStart(2, '0')}.xlsx`
      : `sap-external-access_${days}d.xlsx`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  });

  const rows = useMemo(() => {
    // Sort dijalankan PALING AKHIR, setelah semua saringan. Menyusun ulang
    // dulu lalu menyaring memberi hasil yang sama tetapi mengurutkan baris yang
    // kemudian dibuang.
    const sorted = (list) => (sort.key && SORT_KEYS[sort.key]
      // Salinan dulu: Array.prototype.sort mengubah array di tempat, dan
      // data.rows adalah state — mengurutkannya langsung akan merusak urutan
      // asli secara permanen sehingga klik ketiga tidak bisa memulihkannya.
      ? [...list].sort(cmpRows(sort.key, sort.dir))
      : list);
    let all = data?.rows || [];
    // Baris lama yang tercatat sebelum kolom `source` ada bernilai kosong.
    // Semuanya berasal dari USR41, jadi kosong diperlakukan sebagai SAPGUI —
    // membuangnya dari kedua filter akan menyembunyikan riwayat tanpa sebab.
    if (src === 'http') all = all.filter(r => r.source === 'THUSER');
    else if (src === 'gui') all = all.filter(r => r.source !== 'THUSER');
    if (cls) all = all.filter(r => r.net_class === cls);
    const needle = q.trim().toLowerCase();
    if (!needle) return sorted(all);
    // Jalur akses dan sumber IKUT dicari. Keduanya terbaca jelas di tabel
    // sebagai "SAPROUTER" dan "HTTP", jadi mengetiknya di kotak pencarian
    // adalah gerakan yang wajar — dan sebelum ini selalu berakhir nihil, yang
    // terbaca seolah datanya tidak ada.
    const src_ = r => (r.source === 'THUSER' ? 'HTTP' : 'SAPGUI');
    return sorted(all.filter(r => [r.bname, r.full_name, r.owner_name, r.owner_email, r.ip, r.hostname,
      r.device_type, r.company, r.city, r.district, r.dept_code,
      r.net_class, src_(r), r.http_via, r.origin_ip, r.origin_rdns]
      .some(v => String(v || '').toLowerCase().includes(needle))));
  }, [data, q, src, cls, sort]);

  // Tiga keadaan per kolom: naik, turun, lalu kembali ke urutan server.
  // Menekan kolom LAIN selalu dimulai dari naik, tidak mewarisi arah kolom
  // sebelumnya — arah yang terbawa membuat kolom baru tampil terurut menurun
  // tanpa pembaca pernah memintanya.
  const toggleSort = useCallback((key) => {
    setSort(prev => (prev.key !== key ? { key, dir: 'asc' }
      : prev.dir === 'asc' ? { key, dir: 'desc' }
      : { key: '', dir: 'asc' }));
  }, []);

  // Ringkasan mengikuti filter sumber, bukan seluruh muatan. Kartu yang
  // menghitung 40 sesi sementara tabel di bawahnya menampilkan 6 adalah cara
  // paling cepat membuat pembaca salah mengutip angka ke atasan.
  // Kartu kelas dihitung dari baris yang sudah disaring SUMBER, tetapi SENGAJA
  // tidak ikut disaring kelas. Kalau ikut, menekan SAPROUTER akan melenyapkan
  // kartu VPN dan EXTERNAL dari layar — dan tidak ada lagi yang bisa ditekan
  // untuk berpindah atau kembali.
  const bySrc = useMemo(() => {
    const all = data?.rows || [];
    if (src === 'http') return all.filter(r => r.source === 'THUSER');
    if (src === 'gui')  return all.filter(r => r.source !== 'THUSER');
    return all;
  }, [data, src]);

  const byClass = useMemo(() => {
    if (src === 'all') return data?.summary?.byClass || {};
    const m = {};
    bySrc.forEach(r => { m[r.net_class] = (m[r.net_class] || 0) + 1; });
    return m;
  }, [data, bySrc, src]);

  // Dua kartu teratas mengikuti SELURUH saringan yang aktif, termasuk kelas dan
  // kotak pencarian — angkanya harus selalu sama dengan jumlah baris di bawah,
  // karena angka itulah yang akan dikutip orang.
  const filtered = cls || q.trim() || src !== 'all';
  const shownUsers = useMemo(
    () => (filtered ? new Set(rows.map(r => r.bname)).size
                    : (data?.summary?.totalUsers ?? '—')), [data, rows, filtered]);

  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  const hset = (k, v) => setHdraft(d => ({ ...d, [k]: v }));
  const dirty = cfg && draft && JSON.stringify(cfg) !== JSON.stringify(draft);
  const hdirty = hcfg && hdraft && JSON.stringify(hcfg) !== JSON.stringify(hdraft);

  return (
    <div style={{ padding: '18px 22px 40px', maxWidth: 1500, margin: '0 auto' }}>

      {/* Judul */}
      <div style={{ background: "linear-gradient(135deg, rgb(var(--brand-600)) 0%, rgb(var(--brand-400)) 100%)", borderRadius: 14, padding: '16px 22px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: 4 }}>
        <h1 style={{ fontSize: 20, fontWeight: 800, color: '#fff', margin: 0, letterSpacing: '-0.4px' }}>{T.title}</h1>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', marginTop: 3 }}>{T.subtitle}</div>
      </div>

      {/* Spanduk metode — permanen, tidak bisa ditutup. Lihat catatan di kepala berkas. */}
      <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start', padding: '10px 13px', borderRadius: 10,
        background: '#f8fafc', border: '1px solid #e2e8f0', margin: '14px 0 16px' }}>
        <Info size={14} color="#64748b" style={{ marginTop: 2, flexShrink: 0 }} />
        <div style={{ fontSize: 11.5, color: '#475569', lineHeight: 1.6 }}>{T.method}</div>
      </div>

      {msg ? (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '9px 13px', borderRadius: 10, marginBottom: 14,
          background: msg.type === 'ok' ? '#f0fdf4' : '#fef2f2',
          border: `1px solid ${msg.type === 'ok' ? '#bbf7d0' : '#fecaca'}`,
          color: msg.type === 'ok' ? '#15803d' : '#b91c1c', fontSize: 12.5 }}>
          {msg.type === 'ok' ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />} {msg.text}
        </div>
      ) : null}

      {/* Kontrol */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end', marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 11.5, fontWeight: 600, color: '#475569', marginBottom: 4 }}>{T.scope}</div>
          <div style={{ display: 'flex', border: '1px solid #e2e8f0', borderRadius: 9, overflow: 'hidden' }}>
            {[['external', T.external], ['all', T.all]].map(([v, l]) => (
              <button key={v} onClick={() => setScope(v)}
                style={{ padding: '7px 14px', fontSize: 12.5, fontWeight: 600, border: 'none', cursor: 'pointer',
                  background: scope === v ? '#167946' : '#fff', color: scope === v ? '#fff' : '#475569' }}>{l}</button>
            ))}
          </div>
        </div>
        {/* Sumber. Dua pengumpul menulis ke tabel yang sama, dan pertanyaan
            "sesi WebGUI saja ada berapa" tidak bisa dijawab tanpa saklar ini.
            Bawaannya Semua: memfilter secara diam-diam akan menyembunyikan
            separuh temuan dari pembaca yang tidak tahu ada dua sumber. */}
        <div>
          <div style={{ fontSize: 11.5, fontWeight: 600, color: '#475569', marginBottom: 4 }}>{T.source}</div>
          <div style={{ display: 'flex', border: '1px solid #e2e8f0', borderRadius: 9, overflow: 'hidden' }}>
            {[['all', T.srcAll, ''], ['gui', T.srcGui, T.srcGuiHint], ['http', T.srcHttp, T.srcHttpHint]].map(([v, l, h]) => (
              <button key={v} onClick={() => setSrc(v)} title={h}
                style={{ padding: '7px 13px', fontSize: 12.5, fontWeight: 600, border: 'none', cursor: 'pointer',
                  background: src === v ? '#167946' : '#fff', color: src === v ? '#fff' : '#475569' }}>{l}</button>
            ))}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 11.5, fontWeight: 600, color: '#475569', marginBottom: 4 }}>{T.range}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="date" value={dFrom} max={dTo}
                  onChange={e => setDFrom(e.target.value || dFrom)}
                  style={{ ...inputStyle, width: 138 }} />
                <span style={{ fontSize: 12, color: '#64748b' }}>&rarr;</span>
                <input type="date" value={dTo} min={dFrom}
                  onChange={e => setDTo(e.target.value || dTo)}
                  style={{ ...inputStyle, width: 138 }} />
          </div>
          {/* Kolom ini pernah ikut menentukan periode email, dan itulah asal
              judul "Monthly report — 2026-08-05 to 2026-08-19". Sekarang tidak
              lagi; keterangannya dibuat eksplisit supaya tidak ada yang
              mengubah angka di sini lalu mengira laporannya ikut berubah. */}
          <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 3, maxWidth: 300 }}>{T.rangeHint}</div>
        </div>
        <Btn onClick={() => loadData(days, scope)} busy={loading} Icon={RefreshCw}>{T.refresh}</Btn>
        <Btn onClick={doPoll} busy={busy === 'poll'} Icon={Send} primary>{T.poll}</Btn>
        {/* Preview report dan Send now DIPINDAH ke blok Manual test di bawah.
            Tombol yang mengirim email ke atasan tidak sepantasnya berdampingan
            dengan Refresh dan Poll now — kedekatan itulah yang membuat kiriman
            tak sengaja mungkin terjadi. Yang tersisa di sini hanya tombol
            penjelajahan, yang semuanya mengikuti kolom Range. */}
        <Btn onClick={doExport} busy={busy === 'export'} Icon={FileSpreadsheet}>{T.exportX}</Btn>
      </div>

      {/* Ringkasan.
          forceOpen saat ada saringan kelas aktif: kartu-kartu ini SEKALIGUS
          kendali saringan, jadi melipatnya akan menyembunyikan sebab tabel di
          bawah menjadi pendek. Tombol lepas-saringan juga dipindah ke header
          panel supaya tetap terjangkau. */}
      <CollapsiblePanel
        id="logonAudit.summary"
        title={T.summaryPanel}
        meta={`${filtered ? rows.length : (data?.count ?? 0)} ${T.sessions.toLowerCase()}`
          + (cls ? ` · ${cls} ${T.filterActive}` : '')}
        forceOpen={!!cls}
        right={cls ? (
          <button onClick={() => setCls('')}
            style={{ padding: '5px 11px', borderRadius: 999, cursor: 'pointer',
              border: '1px solid #e2e8f0', background: '#fff', color: '#475569',
              fontSize: 11.5, fontWeight: 600 }}>
            {T.clearFilter}
          </button>
        ) : null}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9, marginBottom: 16 }}>
        <Chip label={T.sessions} value={filtered ? rows.length : (data?.count ?? '—')} tone="INTERNAL" />
        <Chip label={T.users} value={shownUsers} tone="INTERNAL" />
        {['SAPROUTER', 'VPN', 'EXTERNAL', 'UNKNOWN', 'INTERNAL']
          .filter(k => byClass[k])
          .map(k => (
            <Chip key={k} label={k} value={byClass[k]} tone={k}
              active={cls === k}
              // Menekan kartu yang sudah aktif melepas saringannya. Tanpa ini
              // satu-satunya jalan kembali adalah memuat ulang halaman.
              onClick={() => setCls(c => (c === k ? '' : k))} />
          ))}
      </div>

      {!cls && Object.keys(byClass).length > 1 ? (
        <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: -8, marginBottom: 4 }}>{T.chipHint}</div>
      ) : null}
      </CollapsiblePanel>

      {/* Rincian sesi.
          forceOpen saat kotak cari terisi: hasil pencarian yang tidak terlihat
          adalah pencarian yang gagal, walau lipatannya tersimpan dari kunjungan
          sebelumnya. */}
      <CollapsiblePanel
        id="logonAudit.table"
        title={T.detailPanel}
        meta={loading ? '…' : `${rows.length} ${T.sessions.toLowerCase()}`}
        forceOpen={!!q.trim()}>
      {/* Cari */}
      <div style={{ position: 'relative', marginBottom: 10, maxWidth: 420 }}>
        <Search size={14} color="#94a3b8" style={{ position: 'absolute', left: 10, top: 9 }} />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={T.search}
          style={{ ...inputStyle, paddingLeft: 30, paddingRight: 28 }} />
        {q ? (
          <button onClick={() => setQ('')} aria-label="clear"
            style={{ position: 'absolute', right: 6, top: 6, border: 'none', background: 'transparent',
              color: '#94a3b8', cursor: 'pointer', fontSize: 15, lineHeight: 1, padding: '2px 4px' }}>×</button>
        ) : null}
      </div>

      {/* Tabel */}
      <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {/* Judul kolom dipasangkan ke kunci urut, bukan ke posisinya:
                    urutan kolom di tabel ini sudah pernah bergeser, dan indeks
                    telanjang membuat pergeseran berikutnya mengurutkan kolom
                    yang salah tanpa menimbulkan error apa pun. */}
                {[['user', T.user], ['path', T.path], ['ip', T.ip], ['origin', T.origin],
                  ['device', T.device], ['owner', T.owner], ['company', T.company], ['site', T.site],
                  ['dept', T.dept], ['client', T.client], ['logon', T.logon],
                  ['firstSeen', T.firstSeen], ['lastSeen', T.lastSeen], ['polls', T.polls]]
                  .map(([k, h]) => {
                    const active = sort.key === k;
                    return (
                      <th key={k} aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                        style={{ padding: 0, textAlign: k === 'polls' ? 'center' : 'left',
                          fontWeight: 600, color: '#475569', whiteSpace: 'nowrap', borderBottom: '1px solid #e2e8f0' }}>
                        <button type="button" onClick={() => toggleSort(k)}
                          title={active ? (sort.dir === 'asc' ? T.sortDesc : T.sortOff) : T.sortAsc}
                          style={{ width: '100%', padding: '9px 11px', border: 'none', background: 'transparent',
                            font: 'inherit', color: active ? '#0f172a' : 'inherit', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: 4,
                            justifyContent: k === 'polls' ? 'center' : 'flex-start' }}>
                          {h}
                          {/* Panah hanya muncul pada kolom yang aktif. Menandai
                              semua kolom dengan panah samar membuat 14 judul
                              berebut perhatian dan justru menyembunyikan mana
                              yang sedang mengurutkan tabel. */}
                          <span aria-hidden style={{ fontSize: 9, lineHeight: 1,
                            color: active ? '#0f172a' : '#cbd5e1' }}>
                            {active ? (sort.dir === 'asc' ? '\u25b2' : '\u25bc') : '\u21c5'}
                          </span>
                        </button>
                      </th>
                    );
                  })}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={14} style={{ padding: 26, textAlign: 'center', color: '#94a3b8' }}>
                  <Loader2 size={16} className="animate-spin" style={{ display: 'inline' }} /></td></tr>
              ) : !rows.length ? (
                <tr><td colSpan={14} style={{ padding: 26, textAlign: 'center', color: '#64748b', fontSize: 12.5 }}>
                  {scope === 'external' && !q ? T.noExternal : T.noRows}</td></tr>
              ) : rows.map((r, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  {/* Nama lengkap diletakkan DI BAWAH akunnya, bukan di kolom
                      sendiri: keduanya menjawab pertanyaan yang sama ("siapa")
                      dan memisahkannya hanya menambah lebar tabel yang sudah
                      panjang. */}
                  <td style={{ padding: '8px 11px', color: '#0f172a', whiteSpace: 'nowrap' }}>
                    <div style={{ fontWeight: 600 }}>{r.bname}</div>
                    {r.full_name
                      ? <div style={{ fontSize: 11, fontWeight: 400, color: '#64748b' }}>{r.full_name}</div>
                      : null}
                  </td>
                  {/* Sumber ditempel di bawah badge jalur akses. Lihat catatan
                      pada SourceTag: satu pertanyaan, satu kolom. */}
                  <td style={{ padding: '8px 11px' }}>
                    <PathBadge value={r.net_class} />
                    <SourceTag source={r.source} via={r.http_via} cands={r.http_cands} T={T} />
                  </td>
                  <td style={{ padding: '8px 11px', fontFamily: 'ui-monospace,monospace', color: '#334155' }}>{r.ip || '—'}</td>
                  {/* Asal sebenarnya di balik SAProuter. Status pencocokan
                      ditampilkan apa adanya — sesi yang tidak dapat dipastikan
                      TIDAK diberi IP tebakan, karena pada laporan audit tebakan
                      yang terlihat pasti lebih berbahaya daripada kolom kosong. */}
                  <td style={{ padding: '8px 11px', whiteSpace: 'nowrap' }}>
                    {r.origin_match === 'unique' && r.origin_ip ? (
                      <>
                        <span style={{ fontFamily: 'ui-monospace,monospace', color: '#0f172a' }}>{r.origin_ip}</span>
                        {r.origin_rdns ? <div style={{ fontSize: 10.5, color: '#94a3b8' }}>{r.origin_rdns}</div> : null}
                      </>
                    ) : r.origin_match === 'ambiguous' ? (
                      <span title={T.originAmb} style={{ fontSize: 11, color: '#b45309',
                        background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 999, padding: '2px 8px' }}>
                        ? ×{r.origin_candidates || 2}</span>
                    ) : r.net_class === 'SAPROUTER' ? (
                      <span title={T.originNone} style={{ fontSize: 11, color: '#cbd5e1' }}>—</span>
                    ) : <span style={{ color: '#e2e8f0' }}>·</span>}
                  </td>
                  <td style={{ padding: '8px 11px', color: '#334155', whiteSpace: 'nowrap' }}>
                    {r.device_type ? <span style={{ fontWeight: 600 }}>{r.device_type}</span> : null}
                    {r.device_type && r.hostname ? <span style={{ color: '#94a3b8' }}> · </span> : null}
                    <span style={{ fontFamily: 'ui-monospace,monospace', fontSize: 11.5 }}>{r.hostname || '—'}</span>
                  </td>
                  {/* Pemilik device dari SDP. Tiga keadaan dibedakan dan tidak
                      diratakan: pemilik resmi, tebakan SDP, dan tidak diketahui.
                      Tebakan diberi warna serta tooltip tersendiri karena pada
                      sebagian baris isinya ruang rapat, bukan orang — dan pada
                      laporan audit, tebakan yang tampil seolah fakta lebih
                      berbahaya daripada kolom kosong. */}
                  <td style={{ padding: '8px 11px', whiteSpace: 'nowrap' }}>
                    {r.owner_source === 'assigned' ? (
                      <>
                        <div style={{ color: '#0f172a' }}>{r.owner_name}</div>
                        {r.owner_dept
                          ? <div style={{ fontSize: 10.5, color: '#94a3b8' }}>{r.owner_dept}</div>
                          : null}
                      </>
                    ) : r.owner_source === 'suggested' ? (
                      <span title={T.ownerSuggested} style={{ fontSize: 11.5, color: '#b45309' }}>
                        {r.owner_name} <span style={{ color: '#d97706' }}>(?)</span>
                      </span>
                    ) : (
                      <span title={r.asset_known ? T.ownerNoUser : T.ownerNoAsset}
                        style={{ color: '#cbd5e1' }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: '8px 11px', color: '#475569' }}>{r.company || '—'}</td>
                  <td style={{ padding: '8px 11px', color: '#475569', whiteSpace: 'nowrap' }}>
                    {[r.city, r.district].filter(Boolean).join(' · ') || '—'}</td>
                  <td style={{ padding: '8px 11px', color: '#475569' }}>{r.dept_code || '—'}</td>
                  <td style={{ padding: '8px 11px', color: '#475569' }}>{r.client || '—'}</td>
                  <td style={{ padding: '8px 11px', color: '#475569', whiteSpace: 'nowrap' }}>
                    {fmtSapDate(r.logon_date)} {fmtSapTime(r.logon_time)}</td>
                  <td style={{ padding: '8px 11px', color: '#64748b', whiteSpace: 'nowrap' }}>{fmtDT(r.first_seen)}</td>
                  <td style={{ padding: '8px 11px', color: '#64748b', whiteSpace: 'nowrap' }}>{fmtDT(r.last_seen)}</td>
                  <td style={{ padding: '8px 11px', textAlign: 'center', color: '#64748b' }}>{r.poll_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </CollapsiblePanel>

      {/* Pengaturan */}
      {draft ? (
        <div style={{ marginTop: 26, border: '1px solid #e2e8f0', borderRadius: 12, background: '#fff', padding: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{T.settings}</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 11.5, color: '#64748b' }}>
              <span>{T.lastPoll}: <b style={{ color: '#334155' }}>{cfg?.last_poll_at ? fmtDT(cfg.last_poll_at) : T.never}</b>
                {cfg?.last_poll_detail ? <span style={{ color: '#94a3b8' }}> · {cfg.last_poll_detail}</span> : null}</span>
              <span>{T.lastSent}: <b style={{ color: '#334155' }}>{cfg?.last_sent_period || T.never}</b></span>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(230px,1fr))', gap: 13 }}>
            <Field label={T.connId}>
              <input type="number" value={draft.connection_id || 0}
                onChange={e => set('connection_id', Number(e.target.value) || 0)} style={inputStyle} />
            </Field>
            {/* Mode periode dulu, baru panjang jendelanya. Urutan ini penting:
                kolom "hari" tidak berarti apa-apa sampai pembaca tahu modenya
                bulanan atau bergulir — sebelumnya kolom itu berdiri sendiri dan
                terbaca seolah menentukan jadwal, padahal dalam mode bulanan
                sama sekali tidak dipakai. */}
            <Field label={T.periodMode} hint={T.periodModeHint}>
              <select value={draft.period_mode === 'days' ? 'days' : 'monthly'}
                onChange={e => set('period_mode', e.target.value)} style={inputStyle}>
                <option value="monthly">{T.periodMonthly}</option>
                <option value="days">{T.periodRolling}</option>
              </select>
            </Field>
            <Field label={T.periodDays}
              hint={draft.period_mode === 'days' ? T.periodDaysHintRolling : T.periodDaysHintMonthly}>
              {/* Tidak dinonaktifkan, karena nilainya TETAP dipakai tombol
                  Preview report dan Send now walau jadwalnya bulanan. Yang
                  diredupkan hanya tampilannya, sebagai isyarat bahwa kolom ini
                  tidak memengaruhi kiriman terjadwal. */}
              <input type="number" min={1} max={90} value={draft.period_days || 14}
                onChange={e => set('period_days', Number(e.target.value) || 14)}
                style={{ ...inputStyle,
                  background: draft.period_mode === 'days' ? '#fff' : '#f8fafc',
                  color: draft.period_mode === 'days' ? '#0f172a' : '#64748b' }} />
            </Field>
            <Field label={T.saprouterIps}>
              <input value={draft.saprouter_ips || ''} onChange={e => set('saprouter_ips', e.target.value)} style={inputStyle} />
            </Field>
            <Field label={T.vpnIps}>
              <input value={draft.vpn_ips || ''} onChange={e => set('vpn_ips', e.target.value)} style={inputStyle} />
            </Field>
          </div>

          <div style={{ marginTop: 13 }}>
            <Field label={T.internalCidrs} hint={T.orderNote}>
              <input value={draft.internal_cidrs || ''} onChange={e => set('internal_cidrs', e.target.value)} style={inputStyle} />
            </Field>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(230px,1fr))', gap: 13, marginTop: 13 }}>
            <Field label={T.emailTo}>
              <input value={draft.email_to || ''} onChange={e => set('email_to', e.target.value)} style={inputStyle} />
            </Field>
            <Field label={T.emailCc}>
              <input value={draft.email_cc || ''} onChange={e => set('email_cc', e.target.value)} style={inputStyle} />
            </Field>
            <Field label={T.emailBcc}>
              <input value={draft.email_bcc || ''} onChange={e => set('email_bcc', e.target.value)} style={inputStyle} />
            </Field>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(230px,1fr))', gap: 13, marginTop: 13 }}>
            <Field label={T.reportTitle}>
              <input value={draft.report_title || ''} onChange={e => set('report_title', e.target.value)} style={inputStyle} />
            </Field>
            <Field label={T.preparedBy}>
              <input value={draft.prepared_by || ''} onChange={e => set('prepared_by', e.target.value)} style={inputStyle} />
            </Field>
            <Field label={T.senderName}>
              <input value={draft.sender_name || ''} onChange={e => set('sender_name', e.target.value)} style={inputStyle} />
            </Field>
          </div>

          <div style={{ marginTop: 13 }}>
            <Field label={T.introNote} hint={T.introHint}>
              <textarea rows={3} value={draft.intro_note || ''}
                onChange={e => set('intro_note', e.target.value.slice(0, 1000))}
                style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }} />
            </Field>
          </div>

          {/* Checkbox "Include internal sessions in the Excel attachment"
              DIHAPUS. Sheet 'Internal Detail' sudah tidak ada di lampiran atas
              permintaan atasan, jadi opsi itu tidak berefek apa pun — dan
              setelan yang terlihat aktif tetapi tidak mengubah hasil lebih
              menyesatkan daripada tidak ada sama sekali. Kolom include_internal
              tetap ada di basis data dan tidak dihapus, sehingga nilai lama
              tidak hilang bila suatu saat sheet itu dikembalikan. */}

          <div style={{ marginTop: 16 }}>
            <Btn onClick={doSave} busy={busy === 'save'} disabled={!dirty} Icon={Save} primary>{T.save}</Btn>
          </div>
        </div>
      ) : null}

      {/* Sumber HTTP (nginx).
          Blok TERPISAH dari Pengaturan di atas karena endpoint-nya juga
          terpisah (/api/sap/http-audit/config). Menggabungkannya ke satu form
          akan membuat satu tombol Simpan menulis ke dua tempat, dan kegagalan
          separuh jalan meninggalkan keadaan yang tidak jelas tersimpan atau
          tidak. Kedua tombol di sini — Uji SSH dan Poll HTTP — tidak mengirim
          email dan tidak menyentuh last_sent_period, jadi keduanya tetap netral,
          bukan merah. */}
      {hdraft ? (
        <div style={{ marginTop: 14, border: '1px solid #e2e8f0', borderRadius: 12, background: '#fff', padding: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: 10, flexWrap: 'wrap', gap: 10 }}>
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0f172a',
              display: 'flex', alignItems: 'center', gap: 7 }}>
              <Network size={16} color="#167946" /> {T.httpTitle}
            </h2>
            <div style={{ fontSize: 11.5, color: '#64748b' }}>
              {T.lastPoll}: <b style={{ color: '#334155' }}>
                {hcfg?.last_poll_at ? fmtDT(hcfg.last_poll_at) : T.never}</b>
              {hcfg?.last_poll_status ? <span style={{ color: '#94a3b8' }}> · {hcfg.last_poll_status}</span> : null}
            </div>
          </div>

          <div style={{ fontSize: 11.5, color: '#475569', lineHeight: 1.6, marginBottom: 12 }}>{T.httpIntro}</div>

          {/* ssh2 tidak terpasang berarti seluruh blok ini tidak akan bekerja
              betapapun benar isinya. Dikatakan di depan, bukan dibiarkan muncul
              sebagai galat saat tombol ditekan. */}
          {hcfg && hcfg.ssh2 === false ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '9px 13px', borderRadius: 10,
              marginBottom: 12, background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontSize: 12 }}>
              <AlertTriangle size={14} /> {T.ssh2Missing}
            </div>
          ) : null}

          {/* Statistik ringkas. Sebaran http_via adalah alasan blok ini ada:
              proporsi ambigu yang tinggi berarti korelasi berbasis waktu sudah
              tidak memadai, dan itu hanya terlihat kalau angkanya ditampilkan. */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9, marginBottom: 14 }}>
            <Chip label={T.httpStats}
              value={(hstats?.by_via || []).reduce((a, b) => a + (b.n || 0), 0) || 0} tone="INTERNAL"
              onClick={() => openDetail('all')} />
            <Chip label={T.nginxHits} value={hstats?.nginx_hits?.n ?? 0} tone="INTERNAL"
              onClick={() => openDetail('hits')} />
            {(hstats?.by_via || []).map(v => (
              <Chip key={v.http_via || 'null'} label={v.http_via || '—'} value={v.n}
                tone={String(v.http_via || '').startsWith('proxy-') ? 'UNKNOWN' : 'INTERNAL'}
                onClick={() => openDetail(v.http_via || 'all')} />
            ))}
          </div>
          {(hstats?.by_via || []).some(v => v.http_via === 'proxy-ambiguous') ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '9px 13px', borderRadius: 10,
              marginBottom: 14, background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e',
              fontSize: 11.5, lineHeight: 1.6 }}>
              <HelpCircle size={14} style={{ marginTop: 2, flexShrink: 0 }} /> {T.ambWatch}
            </div>
          ) : null}

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 9, marginBottom: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!Number(hdraft.enabled)}
              onChange={e => hset('enabled', e.target.checked ? 1 : 0)} style={{ marginTop: 2 }} />
            <span>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: '#0f172a' }}>{T.httpEnabled}</div>
              <div style={{ fontSize: 10.5, color: '#94a3b8' }}>{T.httpEnabledHint}</div>
            </span>
          </label>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(230px,1fr))', gap: 13 }}>
            <Field label={T.nginxHost}>
              <input value={hdraft.nginx_host || ''} onChange={e => hset('nginx_host', e.target.value)} style={inputStyle} />
            </Field>
            <Field label={T.nginxPort}>
              <input type="number" value={hdraft.nginx_port || 22}
                onChange={e => hset('nginx_port', Number(e.target.value) || 22)} style={inputStyle} />
            </Field>
            <Field label={T.nginxUser}>
              <input value={hdraft.nginx_user || ''} onChange={e => hset('nginx_user', e.target.value)} style={inputStyle} />
            </Field>
            {/* Nama berkas, bukan isi kunci. Kunci privatnya tidak pernah
                meninggalkan server, jadi kolom ini teks biasa — menyamarkannya
                jadi field password hanya menyiratkan rahasia yang tidak ada. */}
            <Field label={T.nginxKey} hint={T.nginxKeyHint}>
              <input value={hdraft.nginx_key_file || ''} onChange={e => hset('nginx_key_file', e.target.value)}
                style={{ ...inputStyle, fontFamily: 'ui-monospace,monospace' }} />
            </Field>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 13, marginTop: 13 }}>
            <Field label={T.nginxLog}>
              <input value={hdraft.nginx_log_path || ''} onChange={e => hset('nginx_log_path', e.target.value)}
                style={{ ...inputStyle, fontFamily: 'ui-monospace,monospace' }} />
            </Field>
            <Field label={T.proxyIp} hint={T.proxyIpHint}>
              <input value={hdraft.proxy_ip || ''} onChange={e => hset('proxy_ip', e.target.value)}
                style={{ ...inputStyle, fontFamily: 'ui-monospace,monospace' }} />
            </Field>
            <Field label={T.pathMarker} hint={T.pathMarkerHint}>
              <input value={hdraft.path_marker || ''} onChange={e => hset('path_marker', e.target.value)}
                style={{ ...inputStyle, fontFamily: 'ui-monospace,monospace' }} />
            </Field>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 13, marginTop: 13 }}>
            <Field label={T.winBack} hint={T.winHint}>
              <input type="number" min={0} max={3600} value={hdraft.window_back ?? 90}
                onChange={e => hset('window_back', Number(e.target.value) || 0)} style={inputStyle} />
            </Field>
            <Field label={T.winFwd}>
              <input type="number" min={0} max={3600} value={hdraft.window_fwd ?? 90}
                onChange={e => hset('window_fwd', Number(e.target.value) || 0)} style={inputStyle} />
            </Field>
            <Field label={T.wibOffset} hint={T.wibOffsetHint}>
              <input type="number" min={-12} max={14} value={hdraft.wib_offset_hours ?? 7}
                onChange={e => hset('wib_offset_hours', Number(e.target.value) || 0)} style={inputStyle} />
            </Field>
            <Field label={T.gapWarn} hint={T.gapWarnHint}>
              <input type="number" min={1} max={240} value={hdraft.gap_warn_hours ?? 24}
                onChange={e => hset('gap_warn_hours', Number(e.target.value) || 24)} style={inputStyle} />
            </Field>
          </div>

          {hcfg?.last_poll_detail ? (
            <div style={{ marginTop: 13, fontSize: 11, color: '#64748b', fontFamily: 'ui-monospace,monospace',
              background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 11px' }}>
              {hcfg.last_poll_detail}
            </div>
          ) : null}

          <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap', marginTop: 16 }}>
            <Btn onClick={doSaveHttp} busy={busy === 'hsave'} disabled={!hdirty} Icon={Save} primary>{T.saveHttp}</Btn>
            <Btn onClick={doTestSsh} busy={busy === 'ssh'} Icon={Plug}>{T.testSsh}</Btn>
            <Btn onClick={doPollHttp} busy={busy === 'hpoll'} Icon={Monitor}>{T.pollHttp}</Btn>
          </div>

          {sshOut !== null ? (
            <pre style={{ marginTop: 12, marginBottom: 0, padding: '10px 12px', borderRadius: 8, background: '#0f172a',
              color: '#e2e8f0', fontSize: 11, lineHeight: 1.6, overflowX: 'auto', whiteSpace: 'pre-wrap' }}>
              {sshOut || '(kosong)'}
            </pre>
          ) : null}
        </div>
      ) : null}

      <HttpDetailModal open={!!det} kind={det} data={detData} loading={detBusy}
        onClose={() => { setDet(''); setDetData(null); }} T={T} />

      {/* Manual test — mengikuti pola halaman Monthly Report.
          Ketiga tombol di sini bekerja pada SATU BULAN, bukan pada kolom Range,
          sehingga apa yang diperiksa memang apa yang akan terkirim. Send now
          ditempatkan di sini, terpisah dari tombol penjelajahan di atas, dan
          diberi warna merah karena satu-satunya tombol di halaman ini yang
          benar-benar mengirim email keluar. */}
      {draft ? (
        <div style={{ marginTop: 14, border: '1px solid #e2e8f0', borderRadius: 12, background: '#fff', padding: 18 }}>
          <div style={{ fontSize: 14.5, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>
            &#129514; {T.manualTest}
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 10 }}>
            <div>
              <div style={{ fontSize: 11.5, fontWeight: 600, color: '#475569', marginBottom: 4 }}>{T.mtYear}</div>
              <input type="number" value={ym.year} placeholder="2026"
                onChange={e => setYm(v => ({ ...v, year: e.target.value }))}
                style={{ ...inputStyle, width: 110 }} />
            </div>
            <div>
              <div style={{ fontSize: 11.5, fontWeight: 600, color: '#475569', marginBottom: 4 }}>{T.mtMonth}</div>
              <input type="number" min={1} max={12} value={ym.month} placeholder="8"
                onChange={e => setYm(v => ({ ...v, month: e.target.value }))}
                style={{ ...inputStyle, width: 110 }} />
            </div>
            <Btn onClick={doPreview} busy={busy === 'preview'} Icon={Eye}>{T.preview}</Btn>
            <Btn onClick={doExportMonth} busy={busy === 'xlsxMonth'} Icon={FileSpreadsheet}>{T.mtXlsx}</Btn>
            {/* Satu-satunya tombol yang mengirim email. Konfirmasi tetap
                dijalankan di doSend, termasuk peringatan bila periodenya kosong. */}
            <Btn onClick={doSend} busy={busy === 'send'} Icon={Send} danger>{T.send}</Btn>
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.6 }}>{T.mtHint}</div>
        </div>
      ) : null}

      {/* Jadwal otomatis.
          Sejak Sep 2026 panel ini juga terbuka untuk pemegang permission
          sap_logon_audit: CRON_KEY_PERM di server.js memetakan sap_logon_poll,
          sap_router_poll dan sap_logon_report ke permission itu, sehingga
          simpan/run tidak lagi menghasilkan 403 untuk non-admin. */}
      <div style={{ marginTop: 26 }}>
        <h2 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{T.schedules}</h2>
        {(user?.role === 'admin' || user?.permissions?.includes('sap_logon_audit')) ? (
          <>
            <div style={{ fontSize: 11.5, color: '#64748b', margin: '8px 0 6px' }}>{T.schedPoll}</div>
            <CronScheduler api={cronApi} jobKey="sap_logon_poll" runConfirm={T.pollConfirm} />
            <div style={{ fontSize: 11.5, color: '#64748b', margin: '16px 0 6px' }}>{T.schedRouter}</div>
            <CronScheduler api={cronApi} jobKey="sap_router_poll" runConfirm={T.routerConfirm} />
            <div style={{ fontSize: 11.5, color: '#64748b', margin: '16px 0 6px' }}>{T.schedReport}</div>
            <CronScheduler api={cronApi} jobKey="sap_logon_report" runConfirm={T.reportConfirm} />
          </>
        ) : (
          <div style={{ fontSize: 12, color: '#64748b', padding: '10px 13px', borderRadius: 10,
            background: '#f8fafc', border: '1px solid #e2e8f0', marginTop: 8 }}>{T.adminOnly}</div>
        )}
      </div>

      {/* Pratinjau */}
      {preview ? (
        <div onClick={() => setPreview(null)}
          style={{ position: 'fixed', inset: 0, background: '#0f172a99', display: 'flex',
            alignItems: 'center', justifyContent: 'center', padding: 24, zIndex: 60 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 14, maxWidth: 960, width: '100%', maxHeight: '86vh',
              overflow: 'auto', padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 14.5, fontWeight: 700, color: '#0f172a' }}>{T.previewTitle}</div>
                <div style={{ fontSize: 11.5, color: '#64748b' }}>{preview.subject}</div>
              </div>
              <button onClick={() => setPreview(null)}
                style={{ border: '1px solid #e2e8f0', background: '#fff', borderRadius: 8, padding: '5px 12px',
                  fontSize: 12.5, cursor: 'pointer', color: '#475569' }}>✕</button>
            </div>
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 14 }}
              dangerouslySetInnerHTML={{ __html: preview.html }} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
